import type { JSX, ReactNode } from 'react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';

/**
 * Gradient hero strip — the admin equivalent of the operator console's
 * HeroPanel. Composes title + subtitle + optional KPI children + optional
 * actions into a single elevated panel with a colored top accent line.
 *
 * Use this on the top of any page that wants a richer landing than just
 * `<PageHeader>`. KPIs live in a flex row beneath the title.
 *
 * Color tokens come from `styles.css` — pass an `accent` color (hex / rgba)
 * to drive the top strip and the icon disk tint.
 */
export type HeroBand = 'healthy' | 'degraded' | 'warning' | 'critical' | 'neutral';

export function HeroPanel({
  title,
  subtitle,
  icon: Icon,
  accent = '#818cf8',
  kpis,
  actions,
  className,
}: {
  title: string;
  subtitle?: ReactNode;
  icon?: LucideIcon;
  accent?: string;
  kpis?: ReactNode;
  actions?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={clsx('hero-panel', className)}
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 16,
        background: 'var(--bg-raised)',
        border: '1px solid var(--border)',
        padding: '20px 22px 18px',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at top left, ${accent}26, transparent 55%)`,
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0, right: 0, top: 0,
          height: 2,
          background: `linear-gradient(90deg, ${accent}, ${accent}66)`,
        }}
      />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '1 1 280px', minWidth: 0 }}>
          {Icon && (
            <div
              aria-hidden
              style={{
                flex: 'none',
                width: 44,
                height: 44,
                borderRadius: 12,
                background: `${accent}26`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 0 0 1px ${accent}40`,
              }}
            >
              <Icon width={22} height={22} color={accent} />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.15 }}>{title}</h2>
            {subtitle && (
              <p className="muted" style={{ margin: '6px 0 0', fontSize: '0.875rem' }}>{subtitle}</p>
            )}
          </div>
        </div>
        {actions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>
      {kpis && (
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
            marginTop: 18,
            paddingTop: 16,
            borderTop: '1px solid var(--border)',
          }}
        >
          {kpis}
        </div>
      )}
    </div>
  );
}

/**
 * Compact KPI tile for the HeroPanel kpis slot. Same shape as the
 * existing MetricCard but inline (no Card wrapping) and with a band
 * indicator chip instead of a free-form trend.
 */
export function HeroKpi({
  label,
  value,
  hint,
  band,
  href,
}: {
  label: string;
  value: string | number;
  hint?: string;
  band?: HeroBand;
  href?: string;
}): JSX.Element {
  const tone = bandColor(band);
  const Wrap: 'a' | 'div' = href ? 'a' : 'div';
  return (
    <Wrap
      {...(href ? { href } : {})}
      style={{
        display: 'block',
        padding: '10px 12px',
        borderRadius: 10,
        background: 'var(--bg-overlay)',
        border: '1px solid var(--border)',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', fontWeight: 600 }}>
          {label}
        </span>
        {band && (
          <span
            style={{
              fontSize: '0.6rem',
              padding: '1px 6px',
              borderRadius: 999,
              background: tone.bg,
              color: tone.fg,
              border: `1px solid ${tone.border}`,
              textTransform: 'capitalize',
            }}
          >
            {band}
          </span>
        )}
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </Wrap>
  );
}

function bandColor(band?: HeroBand): { fg: string; bg: string; border: string } {
  switch (band) {
    case 'healthy':
      return { fg: 'var(--success)', bg: 'var(--success-soft)', border: 'rgba(16,185,129,0.3)' };
    case 'degraded':
    case 'warning':
      return { fg: 'var(--warning)', bg: 'var(--warning-soft)', border: 'rgba(245,158,11,0.3)' };
    case 'critical':
      return { fg: 'var(--danger)', bg: 'var(--danger-soft)', border: 'rgba(239,68,68,0.3)' };
    default:
      return { fg: 'var(--text-muted)', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.3)' };
  }
}
